import { createTheme, Theme, ThemeOptions } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import typography from "./Typography";
import shadows from "./Shadows";
import getThemedComponents from "./Override";

/**
 * App shell dimensions, per the design system's §08: a 264px sidebar rail and a
 * 64px top bar. Both were narrower/shorter (224/50) before the Playful flip —
 * the sidebar because its rows were bare text rather than the 40px pills it now
 * carries, and the top bar because it held a denser, smaller-type header.
 *
 * `SidebarWidth` is ONE OF THREE representations of the same width. The sidebar
 * sets its own width from this constant, while the main content clears it with
 * `tokens.space.px224` and `tokens.space.sidebarOpenPaddingLeft` — so all three
 * must change together or the content renders underneath the rail. (That is
 * exactly what happened when this was first raised to 264 on its own.)
 */
const SidebarWidth = 264;
const TopbarHeight = 64;

const baseTheme: ThemeOptions = {
  palette: {
    mode: "light",
    /**
     * Playful's primary teal, named exactly as its palette sheet names it:
     * `main` for buttons, links and active text; `dark` for FABs, filled step
     * dots and hover; `darker` for banners and filled track bars.
     */
    primary: {
      main: "#0094A4",
      light: "#1EC5D5",
      dark: "#007380",
      darker: "#004653",
      contrastText: "#ffffff",
    },
    /**
     * Playful names `secondary` #004653 — the deep teal. It used to hold
     * #222f30, a near-black that is really a *surface* tone, and `neutral.dark`
     * below now carries that role explicitly. The two were conflated because
     * `secondary` has ~95 call sites doing both jobs; splitting them is what
     * lets this take the value Playful actually specifies.
     */
    secondary: {
      main: "#004653",
      light: "#4a5859",
      dark: "#000507",
      contrastText: "#ffffff",
    },
    /**
     * Playful's dark surface ramp: `dark` is the sidebar and app header band,
     * `darkest` the active sidebar row, `medium` a lighter stop not yet
     * consumed by a call site. `dark` duplicates the
     * value `secondary` carries — kept as its own name
     * because the palette sheet names it, and because `secondary` is used for
     * far more than surfaces at its 95 call sites.
     */
    neutral: {
      medium: "#3E5354",
      dark: "#222F30",
      darkest: "#16201F",
    },
    /** Playful's section-header orange. */
    accent: {
      main: "#E0642A",
      light: "#EE8B5C",
      dark: "#B44A18",
      contrastText: "#FFFFFF",
    },
    success: {
      main: "#4FA98E",
      light: "#b5fee7",
      dark: "#539a85",
      contrastText: "#ffffff",
    },
    /** Playful's informational blue. `light`/`dark` are unspecified by the
     * palette sheet and keep the stops the app already rendered. */
    info: {
      main: "#1B5FA8",
      light: "#5ea2d7",
      dark: "#004976",
      contrastText: "#ffffff",
    },
    error: {
      main: "#E57373",
      light: "#ff9692",
      dark: "#C0392B",
      contrastText: "#ffffff",
    },
    /** Playful's amber. As with `info`, only `main` is specified. */
    warning: {
      main: "#F5A623",
      light: "#ffe8a9",
      dark: "#c1864c",
      contrastText: "#ffffff",
    },
    text: {
      /**
       * Playful's ink pair. `secondary` is the visible one: it rendered
       * #222f30 before this flip — near-identical to primary — so "secondary
       * text" gave almost no hierarchy anywhere it was used. #6B7677 restores
       * it, and lightens roughly 37 call sites at once.
       */
      primary: "#1F2A2B",
      secondary: "#6B7677",
      /**
       * Playful's disabled ink — a hair cooler and lighter than the #a5a5a5
       * this held, which mirrored `grey[500]`. `action.disabled` and
       * `grey[500]` keep #a5a5a5: Playful specifies this value for disabled
       * *text* only, and the control-level disabled tones are a separate role.
       */
      disabled: "#9AA3A3",
    },
    /**
     * The warm neutral ladder, mapped onto MUI's numeric `grey` slots.
     *
     * Every value here used to be a cool grey inherited from the Sass era
     * (#F0F1F5, #F6F6F6, #E5E5E5, #C3C3C3, #707070, …). The design system is
     * explicit that the warm ladder is the whole permitted set — "nothing
     * outside this set" — and a cool grey beside `#EAE8E4` and `#EEE2CF` reads
     * as a different, colder system bleeding through.
     *
     * Each slot keeps the ROLE its comment already described and takes the
     * ladder tone for that role, so the ~30 `grey[…]` call sites outside
     * `src/theme/` keep meaning what they meant. Two slots land on the same
     * `#F4F3F0`: the ladder has one inset-surface tone and the old ramp had
     * two near-identical light greys for it. The full ladder — including the
     * roles with no `grey` slot, like the `#C0B49F` chevron tone — is in
     * `tokens.designSystem.ladder`.
     */
    grey: {
      /**
       * Border/backing tint for avatars and logos (the global `.logo` rule's
       * 1px border). The card hairline is exactly this role.
       */
      50: "#EEE2CF",
      /** Light surface — the ladder's table-header / input-prefix tone. */
      100: "#FAF6EE",
      /** Background for "selected" states. Ladder: inset panel. */
      200: "#F4F3F0",
      /** Background for "disabled" states. Ladder: disabled field fill. */
      300: "#e4e4e4",
      /**
       * Border/line for "disabled" states, and the scrollbar thumb. Ladder:
       * `controlLine` — the same tone `tokens.color.inputBorder` carries for a
       * field's resting outline, which is the same job.
       */
      400: "#c9c7c3",
      /** Text color for "disabled" states. Ladder: disabled text / meta. */
      500: "#9AA3A3",
      /**
       * Icon glyphs, table cells and secondary rows. Was #707070, a cool
       * mid-grey doing this job plus the scrollbar's (now grey.400).
       */
      600: "#4A5556",
      /**
       * Chips background for the date range selector — an inset surface, so
       * the same ladder tone as grey.200 rather than a bespoke light grey.
       */
      700: "#F4F3F0",
      /**
       * The muted reading gray for long-form copy inside cards: hints and
       * secondary body text that must stay distinguishable from the grey.600
       * controls above.
       */
      800: "#39444A",
    },
    background: {
      /**
       * The page ground the Playful mockups actually use — a warm greige, not
       * the lighter cream the design-system doc lists. Cards stay white: the
       * MuiPaper override below pins `common.white`, so every Paper-backed
       * surface lifts off the ground rather than blending into it.
       */
      default: "#EAE8E4",
      /**
       * Stated explicitly rather than left to MUI's default. The `MuiPaper`
       * override below already pins `common.white`, so this changes nothing
       * rendered — it makes `background.paper` correct for the call sites and
       * MUI internals that read the palette slot instead of the component.
       */
      paper: "#FFFFFF",
    },
    /** Playful's hairline on cards and panels. */
    divider: "#EEE2CF",
    common: {
      white: "#FFFFFF",
      black: "#000000",
    },
    action: {
      selected: "#f1f1f1",
      disabledBackground: "#e5e5e5",
      disabled: "#a5a5a5",
      hoverOpacity: 0.02,
      hover: "rgba(0, 0, 0, 0.03)",
    },
  },
  /**
   * Playful's card radius, and the scalar MUI multiplies a bare numeric
   * `borderRadius` by — in `sx`, in `unstable_sx`, and in its own unstyled
   * component defaults. It was 5, a pre-Playful value; every radius the app
   * states for itself is either a templated `radii.*` px string or an explicit
   * px literal, so raising it only affects components neither this theme nor a
   * call site pins (Accordion, Autocomplete's popup, Table, and so on), which
   * is exactly where the card radius is the right default.
   */
  shape: {
    borderRadius: 10,
  },
  /**
   * 8px base, unchanged — and deliberately NOT the 12/20/30/42/60 ramp the
   * design system's "Shape & Spacing" card appears to show.
   *
   * Those five numbers are the pixel HEIGHTS of the illustrative bars in that
   * card's diagram (`height:{{ s.h }}` on a 16px-wide swatch), labelled 1×–6×.
   * They are not the token values: the doc states an 8px base in three separate
   * places — `DESIGN_SYSTEM.md`'s "Spacing: 8px base", its "apply the spacing
   * grid — 8px base unit", and the card's own "Spacing — 8px" title — and its
   * real mockup padding (16/18/20/22/24/26px) follows neither 12/20/30/42/60
   * nor a strict 8 grid. Treating decorative bar heights as the scale would
   * re-space every `mt`/`p`/`gap` multiplier in the app off a diagram.
   */
  spacing: 8,
  /**
   * Both branches use `TopbarHeight`. The narrow one used to hardcode 50px,
   * which matched only because `TopbarHeight` was also 50 — so raising the
   * constant to the design system's 64 desynced them, and `MainLayout`'s
   * `paddingBottom: TopbarHeight` (which clears the mobile bottom bar, the same
   * Toolbar repositioned) started reserving 14px more than the bar occupies.
   * There is one top bar height, so it is named once.
   */
  mixins: {
    toolbar: {
      "@media(min-width:1280px)": {
        minHeight: TopbarHeight,
        padding: "0 30px",
      },
      "@media(max-width:1280px)": {
        minHeight: TopbarHeight,
      },
    },
  },
  /**
   * A breakpoint is the range of predetermined screen sizes that have specific
   * layout requirements.
   *
   *  - xs (extra-small): 0px or larger
   *  - sm (small): 475px or larger
   *  - md (medium): 725px or larger
   *  - lg (large): 1050px or larger
   *  - xl (extra-large): 1250px or larger
   */
  breakpoints: {
    values: { xs: 0, sm: 475, md: 725, lg: 1050, xl: 1250 },
  },
  typography,
  shadows,
};

const brandTheme = createTheme(baseTheme);

/**
 * Built once at module load. Component overrides need the assembled theme to
 * read spacing and palette out of, which is why this is two steps.
 */
const theme: Theme = deepmerge(brandTheme, getThemedComponents(brandTheme));

export default theme;
export { TopbarHeight, SidebarWidth, baseTheme };
